import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, humanizeAxiosError, setStoredToken, getStoredToken } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // user: null = loading, false = not authenticated, object = authenticated
  const [user, setUser] = useState(null);

  const checkAuth = useCallback(async () => {
    // If there is no stored token AND no cookie, the /me call will 401 —
    // still attempt because cookie could be present.
    if (!getStoredToken()) {
      // Try once anyway (cookies might work), but skip if it fails silently.
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        return;
      } catch (_) {
        setUser(false);
        return;
      }
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      setStoredToken(null);
      setUser(false);
    }
  }, []);

  useEffect(() => {
    // Si volvemos del callback de Google OAuth, no verificamos /me aquí:
    // AuthCallback intercambiará el session_id y establecerá la sesión primero.
    if (window.location.hash?.includes("session_id=")) {
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) setStoredToken(data.access_token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: humanizeAxiosError(e) };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      if (data.access_token) setStoredToken(data.access_token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: humanizeAxiosError(e) };
    }
  };

  const googleSession = async (sessionId) => {
    try {
      const { data } = await api.post("/auth/google/session", { session_id: sessionId });
      if (data.access_token) setStoredToken(data.access_token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: humanizeAxiosError(e) };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) {
      // ignore
    }
    setStoredToken(null);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, googleSession, logout, refresh: checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
